import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const REFRESH_TOKEN_ENDPOINT = '/api/refresh-token';


const api = axios.create({
  baseURL: apiUrl,
  timeout: 600000,
});


const isTokenExpired = (token) => {
  if (!token) return true;
  const expiry = JSON.parse(atob(token.split('.')[1])).exp;
  const now = Math.floor(new Date().getTime() / 1000);
  return now >= expiry;
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    isRefreshing = false;
    return Promise.reject('No refresh token available');
  }

  try {
    const response = await api.post(REFRESH_TOKEN_ENDPOINT, { refreshToken });
    const { token, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);
    isRefreshing = false;
    processQueue(null, token);
    return token;
  } catch (error) {
    // The whole session goes, role included: the navbar decides "logged in" from the role.
    logout();
    isRefreshing = false;
    processQueue(error, null);
    throw error;
  }
};

// A browser "had a session" when it holds a token or a refresh token at all, whether still valid or
// not. Only then can a refused request mean that the session expired; a visitor who never logged in
// holds neither, and a 401 for them is the plain "not logged in" answer (the route guard sends them
// to the login page without the expiry message).
const SESSION_EXPIRED = 'Session expired. Please login again.';

// Request Interceptor
//
// With a usable token it is sent. With an expired token and a usable refresh token, the token is
// refreshed first. Otherwise the request goes out without an Authorization header and the server
// answers - 200 for the public endpoints (signup, an invitation link, the public program), 401 for
// anything that needs a login. Nothing is cancelled here: cancelling made every public call of a
// visitor fail before it left the browser. A browser that had a session which can no longer be
// used (both tokens expired, or the refresh refused) has its tokens cleared and the request marked
// (`_sessionExpired`), so the response interceptor can tell that 401 from a visitor's.
api.interceptors.request.use(async (config) => {
  // Skip token check for authenticate and refresh token requests
  if (config.url === REFRESH_TOKEN_ENDPOINT || config.url === '/api/authenticate') {
    return config;
  }

  let token = localStorage.getItem('token');
  const storedRefreshToken = localStorage.getItem('refreshToken');

  if (isTokenExpired(token)) {
    const hadSession = Boolean(token || storedRefreshToken);
    token = null;
    if (storedRefreshToken && !isTokenExpired(storedRefreshToken)) {
      try {
        token = await refreshToken();
      } catch (error) {
        console.error('Token refresh error:', error);
      }
    }
    if (!token && hadSession) {
      // The session is over: drop what is left of it so no stale header is sent and the next
      // request is an ordinary anonymous one, and remember for this request that it expired. The
      // role goes with the tokens (and the navbar is told), or the app would still look logged in.
      logout();
      config._sessionExpired = true;
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Response Interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    console.error('Response error:', error);

    // A rejection that never reached the network carries no `config`; reading originalRequest.url
    // below would throw a TypeError that masks the real error.
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // A 401 here just means "wrong credentials" - not an expired session to
    // recover from. Letting it fall through to the refresh-then-cancel logic
    // below replaced the real error with a misleading "Session expired"
    // message on every failed login attempt. The refresh call's own refusal is
    // handed back to refreshToken() as it is: refreshing again from inside it
    // waited on itself (the queue of the refresh in flight) and never settled.
    if (originalRequest.url === '/api/authenticate' || originalRequest.url === REFRESH_TOKEN_ENDPOINT) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // The session this request belonged to had already ended in the request interceptor.
      if (originalRequest._sessionExpired) {
        throw new axios.Cancel(SESSION_EXPIRED);
      }
      // No session at all: a visitor who is not logged in. The 401 is the answer, not an expiry.
      if (!localStorage.getItem('refreshToken')) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      try {
        const token = await refreshToken();
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // A refresh answer carries no roles (AuthController.refreshToken), so the set stored at
        // login is now one refresh behind. Re-read it from the server. Not for the roles call
        // itself, which would otherwise refresh on its own failure.
        if (originalRequest.url !== '/api/me') refreshRoles();
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Refresh token error:', refreshError);
        throw new axios.Cancel(SESSION_EXPIRED);
      }
    }
    return Promise.reject(error);
  }
);

// Persists an already-issued {token, refreshToken, role} triple, same shape
// /api/authenticate returns. Used both by login() below and by the attendee
// signup/claim flow, which gets its own JWTs directly from the signup
// response body instead of a separate /api/authenticate call.
// Fired whenever the session changes (login, signup, logout) so UI that
// isn't on the stack that triggered the change - the navbar, most notably -
// can react immediately. A route change alone isn't a reliable signal: e.g.
// completing signup stores a session without ever navigating away from
// /signup, so nothing else would tell Header.js to re-read the role.
export const AUTH_CHANGED_EVENT = 'hearts-auth-changed';

// `role` is the account's primary role (used for "where's home for this
// account" type decisions, e.g. Header.js). `roles` is the full set this
// login actually has access to - an account invited as a speaker has
// ['MEMBER','SPEAKER'], not just its primary role (only ADMIN and MEMBER are
// stored on the account since V65) - and is what every access check
// (authority.RoleRoute) tests against. What any endpoint that creates a
// session sends is not trusted for long: the guards and the app's start-up
// sync re-read the set from GET /api/me.
export const storeSession = ({ token, refreshToken, role, roles }) => {
  if (token) localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  if (role) localStorage.setItem('role', role);
  if (roles) localStorage.setItem('roles', JSON.stringify(roles));
  else if (role) localStorage.setItem('roles', JSON.stringify([role]));
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
};

// The roles are computed from the data on every request (J/authority/AuthorityService: ADMIN from
// accounts.role, MEMBER for every account, SPEAKER when the person was asked), and GET /api/me is
// the one route that answers them to the frontend. Nothing decides on the cached set: it is written
// at login, one refresh behind after a token refresh, and a cache written before the switch still
// holds the retired ATTENDEE / SPEAKER names. refreshRoles replaces it with the server's answer. The
// token refresh the request interceptor does before a request does not call it (it has no roles to
// store); the response interceptor's 401 path does. Anonymous and expired sessions are left alone;
// the caller decides what that means.
export const refreshRoles = async () => {
  try {
    const { data } = await api.get('/api/me');
    if (data && Array.isArray(data.roles)) {
      storeSession({ roles: data.roles });
      return data.roles;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const login = async (username, password) => {
  try {
    const response = await api.post('/api/authenticate', { username, password });

    if (response.data && response.data.token && response.data.refreshToken) {
      storeSession(response.data);
      return response.data;
    } else {
      console.error("Login response missing token or refreshToken:", response.data);
      throw new Error("Invalid response from server");
    }
  } catch (error) {
    console.error('Login error:', error);
    if (error.response) {
      console.error('Error response:', error.response.data);
      console.error('Error status:', error.response.status);
      console.error('Error headers:', error.response.headers);
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('role');
  localStorage.removeItem('roles');
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
};

export const getRole = () => localStorage.getItem('role');

// Falls back to a single-role array if 'roles' isn't set - covers a session
// stored before the roles set existed as well as an endpoint that only sends
// the primary `role`. Never treated as authoritative: GET /api/me decides.
export const getRoles = () => {
  try {
    const raw = localStorage.getItem('roles');
    if (raw) return JSON.parse(raw);
  } catch {
    // fall through to the single-role fallback below
  }
  const role = getRole();
  return role ? [role] : [];
};

// The one case where "register for event X" has to survive a login that
// isn't itself token-based: an anonymous visitor with an *existing* account
// clicks "Register", lands on /signup?eventId=X, is told to log in instead,
// and follows that link to /login?eventId=X (see AttendeeSignup's 'exists'
// step). Since the switch the registration is one ATTENDEE engagement on the
// person (D19) and the route is `/api/me/events/{id}/registration`
// (me.MeRegistrationController): 409 when the person is already registered,
// 403 {"error":"EMAIL_NOT_VERIFIED"} for an unconfirmed login, 404 unknown
// event. Its answer is {status, eventId, eventName, eventNameEn}.
export const registerForEvent = async (eventId) => {
  if (!eventId) return null;
  try {
    const { data } = await api.post(`/api/me/events/${eventId}/registration`);
    return { eventId, success: true, eventName: data.eventName, eventNameEN: data.eventNameEn };
  } catch (error) {
    // `status` lets the destination tell the two failures apart without parsing the body: 403
    // {"error": "EMAIL_NOT_VERIFIED"} (the login email is not confirmed yet) and 409 (already
    // registered) answer different strings.
    return { eventId, success: false, status: error.response?.status, error: error.response?.data };
  }
};

// Turns a failed profile-save request into a message that actually tells the
// person what to do next, instead of the same "could not save, try again"
// string regardless of cause - a session-expired retry will never succeed,
// and a dropped connection reads very differently from a real server error.
// `t` is the caller's own react-intl formatter (id, defaultMessage) => string.
export const describeSaveError = (err, t) => {
  if (err instanceof axios.Cancel) {
    return t('profile.saveErrorSession', 'Your session has expired. Please log in again to save your changes.');
  }
  if (!err.response) {
    return t('profile.saveErrorNetwork', 'Could not reach the server. Please check your connection and try again.');
  }
  return t('profile.saveError', 'Could not save your profile. Please try again.');
};

export default api;
